import { Map, Set } from 'immutable';

import { isOpenSizingSellingMethodAllowed } from '../../../product/checkAttribute';
import { isValidNumber } from '../../../number';

import { SELLING_METHOD, VALUE } from '../../../../constants/product';
import { OPERATIONS } from '../../../../constants/bulkEdit';
import { ATTRIBUTE } from '../../../../constants/attributes';
import { EMPTY } from '../../../../constants';
import { FAIRE } from '../../../../constants/channels';
import { FORM } from '../../../../constants/forms';

export function sellingMethod({ bulkEdit, preview }) {
  const shipping = preview.get(ATTRIBUTE.SHIPPING, EMPTY.MAP);
  const selectedSellingMethod = shipping.getIn([ATTRIBUTE.SELLING_METHOD, VALUE]);

  // Send all three operations to ensure DB is in sync and avoid conflicts with Faire API
  const operations = [];
  let edited = null; // null means use all selected products

  switch (selectedSellingMethod) {
    case SELLING_METHOD.SIZE: {
      // "Open sizing" - set per_style_minimum_order_quantity, reset unit_multiplier and minimum_order_quantity
      // Filter to only include products that have a "Size" variation
      if (bulkEdit) {
        const selectedProducts = bulkEdit.getIn(['products', 'selected'], EMPTY.SET);
        const productsById = bulkEdit.getIn(['products', 'byId'], EMPTY.MAP);
        
        edited = selectedProducts.filter(function filterOpenSizingProducts(productId) {
          const product = productsById.get(productId);
          if (!product) return false;
          return isOpenSizingSellingMethodAllowed(product.get(FORM.VARIATIONS, EMPTY.MAP));
        });
      }

      const perStyleValues = shipping.get(ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, EMPTY.MAP);
      const perStyleMoq = isValidNumber(perStyleValues.get(VALUE)) ? parseInt(perStyleValues.get(VALUE)) : 1;
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY.SET,
          value: Math.max(perStyleMoq, 1), // Ensure at least 1 for Open sizing
        })
      );

      // Reset unit_multiplier to 1 (required for Open sizing)
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].CASE_SIZE.SET,
          value: 1,
        })
      );

      // Reset minimum_order_quantity to 0 (not used for Open sizing)
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY.SET,
          value: 0,
        })
      );
      break;
    }

    case SELLING_METHOD.CASE: {
      // "By the case" - set unit_multiplier (min 2) and minimum_order_quantity, reset per_style_minimum_order_quantity
      // unit_multiplier must be > 1 for "By the case", otherwise Faire treats it as "By the item"
      const caseSizeValues = shipping.get(ATTRIBUTE.CASE_SIZE, EMPTY.MAP);
      const enteredCaseSize = isValidNumber(caseSizeValues.get(VALUE)) ? parseInt(caseSizeValues.get(VALUE)) : 2;
      const caseSize = Math.max(enteredCaseSize, 2); // Enforce minimum of 2 for "By the case"
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].CASE_SIZE.SET,
          value: caseSize,
        })
      );

      const moqValues = shipping.get(ATTRIBUTE.MINIMUM_ORDER_QUANTITY, EMPTY.MAP);
      const moq = isValidNumber(moqValues.get(VALUE)) ? parseInt(moqValues.get(VALUE)) : 1;
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY.SET,
          value: Math.max(moq, 1), // Ensure at least 1
        })
      );

      // Reset per_style_minimum_order_quantity to 0 (not used for By the case)
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY.SET,
          value: 0,
        })
      );
      break;
    }

    case SELLING_METHOD.ITEM:
    default: {
      // "By the item" - set unit_multiplier = 1 and minimum_order_quantity, reset per_style_minimum_order_quantity
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].CASE_SIZE.SET,
          value: 1,
        })
      );

      const moqValues = shipping.get(ATTRIBUTE.MINIMUM_ORDER_QUANTITY, EMPTY.MAP);
      const moq = isValidNumber(moqValues.get(VALUE)) ? parseInt(moqValues.get(VALUE)) : 1;
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY.SET,
          value: Math.max(moq, 1), // Ensure at least 1
        })
      );

      // Reset per_style_minimum_order_quantity to 0 (not used for By the item)
      operations.push(
        Map({
          type: OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY.SET,
          value: 0,
        })
      );
      break;
    }
  }

  // Return edited (filtered products) only when it's set (for Open sizing)
  return edited ? { edited, payload: operations } : { payload: operations };
}
